import { CalendarDaysIcon } from '@heroicons/react/24/outline';
import classNames from 'classnames';
import { useField } from 'formik';
import React, { memo, useCallback, useContext, useRef, useState } from 'react';
import { UserContext } from '../../../contexts/UserContext';
import { DateFormats } from '../../../types/graphqlTypes';
import { Input } from '../../inputs/Input';
import { Label } from '../Label';

// The native date input shows the browser's locale, not the user's date
// format. This field is typed in the user's format (dd/mm/yyyy by default)
// and stores an ISO day (yyyy-mm-dd); the calendar button opens the native
// picker for the same value.

const ORDER: Record<DateFormats, ['dd' | 'mm' | 'yyyy', 'dd' | 'mm' | 'yyyy', 'dd' | 'mm' | 'yyyy']> = {
  [DateFormats.Ddmmyyyy]: ['dd', 'mm', 'yyyy'],
  [DateFormats.Mmddyyyy]: ['mm', 'dd', 'yyyy'],
  [DateFormats.Yyyymmdd]: ['yyyy', 'mm', 'dd'],
};

const pad = (n: number) => String(n).padStart(2, '0');

// yyyy-mm-dd → the user's format.
export const formatIsoDay = (iso: string, dateFormat: DateFormats): string => {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(iso);
  if (!match) return '';
  const parts = { yyyy: match[1], mm: match[2], dd: match[3] };
  return ORDER[dateFormat].map((part) => parts[part]).join('/');
};

// The user's format → yyyy-mm-dd, or undefined while it is not a whole valid day.
export const parseDay = (text: string, dateFormat: DateFormats): string | undefined => {
  const parts = text.trim().split(/[/.\-\s]+/);
  if (parts.length !== 3 || parts.some((part) => !/^\d+$/.test(part))) return undefined;
  const values = Object.fromEntries(ORDER[dateFormat].map((part, index) => [part, Number(parts[index])])) as Record<'dd' | 'mm' | 'yyyy', number>;
  if (values.yyyy < 1000 || values.mm < 1 || values.mm > 12 || values.dd < 1 || values.dd > 31) return undefined;
  const date = new Date(Date.UTC(values.yyyy, values.mm - 1, values.dd));
  if (date.getUTCMonth() !== values.mm - 1 || date.getUTCDate() !== values.dd) return undefined;
  return `${values.yyyy}-${pad(values.mm)}-${pad(values.dd)}`;
};

interface DayInputProps {
  // An ISO day (yyyy-mm-dd), or '' for none.
  value: string;
  onChange: (value: string) => void;
  onBlur?: () => void;
  name?: string;
  // ISO days.
  min?: string;
  max?: string;
  required?: boolean;
  invalid?: boolean;
  className?: string;
  'aria-label'?: string;
}

// The day field on its own, outside a form: typed in the user's date format, the calendar button next to it.
const DayInput_: React.FC<DayInputProps> = ({ value, onChange: onValue, onBlur: onBlurProp, name, min, max, required, invalid, className, 'aria-label': ariaLabel }) => {
  const { dateFormat } = useContext(UserContext);
  const format = dateFormat ?? DateFormats.Ddmmyyyy;
  // What is being typed, kept only while it belongs to the stored day it started
  // from: once the stored day changes (typed to completion, picked, or prefilled)
  // the field shows that day in the user's format.
  const [draft, setDraft] = useState<{ forValue: string; text: string } | undefined>(undefined);
  const stored = value ?? '';
  const text = draft && draft.forValue === stored ? draft.text : formatIsoDay(stored, format);
  const pickerRef = useRef<HTMLInputElement>(null);

  const onChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const iso = parseDay(event.target.value, format);
      if (iso) {
        setDraft(undefined);
        onValue(iso);
      } else if (!required && !event.target.value.trim()) {
        // An optional day can be taken away again by emptying the field.
        setDraft(undefined);
        onValue('');
      } else {
        setDraft({ forValue: stored, text: event.target.value });
      }
    },
    [format, onValue, required, stored],
  );
  // Whatever is typed but not a day goes back to the stored one.
  const onBlur = useCallback(() => {
    onBlurProp?.();
    setDraft(undefined);
  }, [onBlurProp]);
  const onPick = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      if (event.target.value) onValue(event.target.value);
    },
    [onValue],
  );
  const openPicker = useCallback(() => {
    const picker = pickerRef.current;
    if (!picker) return;
    if (typeof picker.showPicker === 'function') picker.showPicker();
    else picker.click();
  }, []);

  return (
    <div className={classNames('relative', className)}>
      <Input
        name={name}
        value={text}
        onChange={onChange}
        onBlur={onBlur}
        placeholder={ORDER[format].join('/')}
        inputMode="numeric"
        autoComplete="off"
        required={required}
        aria-label={ariaLabel}
        className={classNames('pr-10', invalid && 'input-error')}
      />
      <button type="button" onClick={openPicker} className="absolute inset-y-0 right-0 flex items-center px-3 text-gray-500 hover:text-black dark:hover:text-white" aria-label="calendar">
        <CalendarDaysIcon className="h-5 w-5" />
      </button>
      {/* The native picker, kept for its calendar only. */}
      <input ref={pickerRef} type="date" tabIndex={-1} aria-hidden value={stored} min={min} max={max} onChange={onPick} className="pointer-events-none absolute bottom-0 right-0 h-0 w-0 opacity-0" />
    </div>
  );
};
export const DayInput = memo(DayInput_);

interface FormikDayInputProps {
  name: string;
  label?: React.ReactNode;
  // ISO days.
  min?: string;
  max?: string;
  required?: boolean;
  className?: string;
}

const FormikDayInput_: React.FC<FormikDayInputProps> = ({ name, label, min, max, required, className }) => {
  const [field, meta, helpers] = useField<string>(name);
  const invalid = meta.touched && !!meta.error;
  const onChange = useCallback((value: string) => void helpers.setValue(value), [helpers]);
  const onBlur = useCallback(() => void helpers.setTouched(true), [helpers]);

  return (
    <label className={classNames('block relative text-black dark:text-white', className)}>
      <Label>{label}</Label>
      <DayInput name={name} value={field.value ?? ''} onChange={onChange} onBlur={onBlur} min={min} max={max} required={required} invalid={invalid} className="mt-1" />
      <div className="flex items-center h-4 mt-1 text-xs text-error-red-500">{invalid ? meta.error : null}</div>
    </label>
  );
};

export const FormikDayInput = memo(FormikDayInput_);
